import { $http } from "@/assets/js/appFunc";
import { uuid } from "short-uuid";
import store from "@/store";

const algorithmName = "AES-CTR";
const algorithmLength = 128;
const keyName = "PBKDF2";
const walletsStorageKey = "wallets";
let walletsConcat = null;

function arrayBufferToBase64(arrayBuffer) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(arrayBuffer)));
}

function base64ToArrayBuffer(base64string) {
    return Uint8Array.from(atob(base64string), c => c.charCodeAt(0));
}

async function generateKey(password, salt) {
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw",
        password,
        keyName,
        false,
        ["deriveBits", "deriveKey"]
    );

    return window.crypto.subtle.deriveKey(
        {
            name: keyName,
            salt,
            iterations: 100000,
            hash: "SHA-256"
        },
        keyMaterial,
        {
            name: algorithmName,
            length: 256
        },
        true,
        ["encrypt", "decrypt"]
    );
}

export default {
    async encrypt(data, password) {
        const encoder = new TextEncoder();
        const iv = window.crypto.getRandomValues(new Uint8Array(16));
        const key = await generateKey(encoder.encode(password), iv);

        const encryptedBuffer = await window.crypto.subtle.encrypt(
            {
                name: algorithmName,
                length: algorithmLength,
                counter: iv
            },
            key,
            encoder.encode(data)
        );

        return {
            encryptedDataBase64: arrayBufferToBase64(encryptedBuffer),
            ivBase64: arrayBufferToBase64(iv)
        };
    },

    async decrypt(encryptedData, password, iv) {
        let decryptBuffer;
        const encoder = new TextEncoder();
        const decoder = new TextDecoder();
        const key = await generateKey(encoder.encode(password), base64ToArrayBuffer(iv));

        try {
            decryptBuffer = await window.crypto.subtle.decrypt(
                {
                    name: algorithmName,
                    length: algorithmLength,
                    counter: base64ToArrayBuffer(iv)
                },
                key,
                base64ToArrayBuffer(encryptedData)
            );
        } catch (e) {
            throw new Error("Failed decryption");
        }

        return decoder.decode(decryptBuffer);
    },

    async saveWallet(name, data, password, type, accounts, keystore = null, passphrase = "", saveMethod) {
        const wallets = this.getWalletsLocal();
        let encryptedPassphrase;
        let encryptedData;
        let iv;

        if (type === "hd" || type === "privateKey") {
            const { encryptedDataBase64, ivBase64 } = await this.encrypt(data, password);
            encryptedData = encryptedDataBase64;
            iv = ivBase64;
        }

        if (passphrase) {
            const { encryptedDataBase64 } = await this.encrypt(data, password);
            encryptedPassphrase = encryptedDataBase64;
        }

        if (saveMethod === "local") {
            const wallet = {
                id: uuid(),
                name,
                type,
                encryptedData,
                encryptedPassphrase,
                keystore,
                accounts,
                iv
            };
            wallets.push(wallet);
            this.updateWallets(wallet, "save");
            localStorage.setItem(walletsStorageKey, JSON.stringify(wallets));
        } else if (saveMethod === "cloud") {
            const wallet = {
                name,
                type,
                encryptedData,
                encryptedPassphrase,
                keystore,
                accounts,
                iv
            };
            const req = {
                url: "/blockchain/wallets/vault",
                type: "PUT",
                data: wallet
            };
            return $http(req, true).then(res => {
                wallet.id = res.data;
                wallet.onCloud = true;
                this.updateWallets(wallet, "save");
            });
        }
    },

    async getWallet(id, password) {
        let selectedWallet;
        let decryptedPassphrase;
        const wallets = walletsConcat || this.getAllWallets();

        wallets.some(wallet => {
            if (wallet.id === id) {
                selectedWallet = wallet;
                return true;
            }
            return false;
        });

        const decryptedData = await this.decrypt(selectedWallet.encryptedData, password, selectedWallet.iv);

        if (selectedWallet.passphrase) {
            decryptedPassphrase = await this.decrypt(selectedWallet.passphrase, password, selectedWallet.iv);
        }

        return {
            decryptedData,
            decryptedPassphrase
        };
    },
    async getAllWallets(id) {
        if (!walletsConcat) {
            const cloud = await this.getWalletsCloud(id);
            cloud.data.forEach(element => {
                element.onCloud = true;
            });
            walletsConcat = this.getWalletsLocal().concat(cloud.data);
        }

        return walletsConcat;
    },

    getKeystore(id) {
        let selectedWallet;
        const wallets = walletsConcat || this.getAllWallets();

        wallets.some(wallet => {
            if (wallet.id === id) {
                selectedWallet = wallet;
                return true;
            }
            return false;
        });

        return selectedWallet.keystore;
    },
    getWalletsLocal() {
        return JSON.parse(localStorage.getItem(walletsStorageKey)) || [];
    },
    getWalletsCloud(id) {
        const user = store().getters["userProfile/getUserId"] || id;
        const req = {
            type: "GET",
            url: `/blockchain/wallets/users/${user}`
        };
        return $http(req, true);
    },

    editWalletName(id, name, onCloud) {
        let wallets = null;
        let walletChange = null;
        if (onCloud) {
            wallets = walletsConcat || this.getAllWallets();
        } else {
            wallets = this.getWalletsLocal();
        }

        wallets.some(wallet => {
            if (wallet.id === id) {
                wallet.name = name;
                walletChange = wallet;
                return true;
            }
            return false;
        });
        if (onCloud) {
            this.editWalletCloud(walletChange);
        } else {
            this.updateWallets(walletChange, "edit");
            localStorage.setItem(walletsStorageKey, JSON.stringify(wallets));
        }
    },

    updateWallets(value, action) {
        if (action === "save") {
            walletsConcat.push(value);
        } else if (action === "edit") {
            for (let i = 0; i < walletsConcat.length; i++) {
                if (walletsConcat[i].id === value.id) {
                    walletsConcat[i] = value;
                }
            }
        } else if (action === "delete") {
            for (let i = 0; i < walletsConcat.length; i++) {
                if (walletsConcat[i].id === value) {
                    walletsConcat.splice(i, 1);
                }
            }
        }
    },

    editWalletCloud(wallet) {
        const payload = {
            name: wallet.name,
            accounts: wallet.accounts
        };
        const req = {
            url: `/blockchain/wallets/${wallet.id}/update-info`,
            type: "PUT",
            data: payload
        };
        return $http(req, true).then(
            data => {
                this.updateWallets(data, "edit");
            }, error => error
        );
    },

    editAccountName(index, walletId, name, onCloud) {
        let wallets = null;
        let walletChange = null;
        if (onCloud) {
            wallets = walletsConcat || this.getAllWallets();
        } else {
            wallets = this.getWalletsLocal();
        }

        wallets.some(wallet => {
            if (wallet.id === walletId) {
                wallet.accounts[index].name = name;
                walletChange = wallet;
                return true;
            }
            return false;
        });

        if (onCloud) {
            this.editWalletCloud(walletChange);
        } else {
            this.updateWallets(walletChange, "edit");
            localStorage.setItem(walletsStorageKey, JSON.stringify(wallets));
        }
    },

    removeAccount(walletId, accountIndex) {
        const wallets = this.getWalletsLocal();

        wallets.some(wallet => {
            if (wallet.id === walletId) {
                wallet.accounts.splice(accountIndex, 1);
                return true;
            }
            return false;
        });

        localStorage.setItem(walletsStorageKey, JSON.stringify(wallets));
    },

    removeWallet(id) {
        const wallets = this.getWalletsLocal();
        const remainingWallets = wallets.filter(wallet => wallet.id !== id);
        this.updateWallets(id, "delete");
        localStorage.setItem(walletsStorageKey, JSON.stringify(remainingWallets));
    },
    deleteWalletAction(walletId) {
        const req = {
            url: `/identity/tenants/users/wallets/${walletId}`,
            type: "DELETE"
        };
        return $http(req, true).then(
            data => {
                this.updateWallets(walletId, "delete");
                return data;
            },
            error => error
        );
    }
};
